// src/components/ErrorBoundary.tsx
import { Component, ReactNode, ErrorInfo } from 'react';
import { Paper, Text, Button, Stack, Group } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface ErrorBoundaryProps {
	children: ReactNode;
	fallbackTitle?: string;
	fallbackMessage?: string;
	showReload?: boolean;
	onReset?: () => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		this.setState({ errorInfo });
		// Log to console in development
		console.error('ErrorBoundary caught an error:', error, errorInfo);
	}

	handleReset = (): void => {
		this.setState({ hasError: false, error: null, errorInfo: null });
		// Optionally call parent reset handler
		if (this.props.onReset) {
			this.props.onReset();
		}
	};

	render(): ReactNode {
		if (this.state.hasError) {
			// Fallback UI
			return (
				<Paper p='xl' radius='md' withBorder>
					<Stack align='center' gap='md'>
						<IconAlertTriangle size={48} color='#FA5252' />
						<Text size='lg' fw={600} ta='center'>
							{this.props.fallbackTitle || 'Something went wrong'}
						</Text>
						<Text size='sm' c='dimmed' ta='center'>
							{this.props.fallbackMessage ||
								'An unexpected error occurred. Please try again.'}
						</Text>
						<Group>
							<Button
								leftSection={<IconRefresh size={16} />}
								onClick={this.handleReset}
								variant='light'
							>
								Try Again
							</Button>
							{this.props.showReload && (
								<Button
									variant='subtle'
									onClick={() => window.location.reload()}
								>
									Reload Page
								</Button>
							)}
						</Group>
					</Stack>
				</Paper>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
